import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { ExternalRedirectService } from 'app/core/services/external-redirect.service';
import { NERA_AUTH_SERVICE } from 'app/core/tokens';
import {
    hasAnyRole,
    ORG_ADMIN_AREA_ROLES,
    PLATFORM_STAFF_ROLES,
    ROLES,
} from 'app/shared/constants';
import { environment } from 'environments/environment';
import { of, switchMap } from 'rxjs';

/**
 * Guard used on the default route ('') to redirect users based on their role.
 *
 * Default redirection map:
 *   - Pure platform staff (SUPER_ADMIN or PLATFORM_ADMIN, no org-admin hat)
 *     → external nera-sa-portal (fe-web has no SA-only internal pages left;
 *     see T3.2/T3.3 of the fe-web → sa-portal migration)
 *   - Org-admin tier (ORG_OWNER/ORG_OPERATOR, alone or combined with
 *     platform staff) → /overview
 *   - PHOTOGRAPHER → /overview
 *   - fallback (any other authenticated user) → /me
 *   - Not authenticated → /sign-in (which hands off to IAM)
 *
 * @example
 * {
 *   path: '',
 *   canActivate: [RoleRedirectGuard],
 *   component: EmptyComponent, // or loadComponent
 * }
 */
export const RoleRedirectGuard: CanActivateFn = (_route, _state) => {
    const authService = inject(NERA_AUTH_SERVICE);
    const router = inject(Router);
    const externalRedirect = inject(ExternalRedirectService);

    return authService.check().pipe(
        switchMap((auth) => {
            if (!auth.isAuthenticated || !auth.user) {
                console.warn(
                    '[RoleRedirectGuard] Not authenticated → /sign-in'
                );
                return of(
                    router.createUrlTree(['/sign-in'], {
                        queryParams: { returnUrl: '/signed-in-redirect' },
                    })
                );
            }

            const roles = auth.user.roles;

            // Pure platform staff (SUPER_ADMIN or PLATFORM_ADMIN — neither
            // carries an org tier, design §2.9) with no org-admin hat →
            // nera-sa-portal. External URL: Router guards can only return a
            // boolean/UrlTree, never an external redirect, so navigate via
            // globalThis.location and block the internal navigation with
            // `false`.
            if (
                hasAnyRole(roles, [...PLATFORM_STAFF_ROLES]) &&
                !hasAnyRole(roles, [...ORG_ADMIN_AREA_ROLES])
            ) {
                const saPortalUrl = environment.saPortalUrl.replace(/\/$/, '');
                externalRedirect.assign(saPortalUrl);
                return of(false);
            }

            // Org-admin tier (ORG_OWNER/ORG_OPERATOR, alone or combined with
            // platform staff) → Org Overview
            if (hasAnyRole(roles, [...ORG_ADMIN_AREA_ROLES])) {
                return of(router.createUrlTree(['/overview']));
            }

            // PHOTOGRAPHER → Overview (role-tailored dashboard: personal album pipeline + quota)
            if (hasAnyRole(roles, [ROLES.PHOTOGRAPHER])) {
                return of(router.createUrlTree(['/overview']));
            }

            // Default — personal PhotoFlow info page
            return of(router.createUrlTree(['/me']));
        })
    );
};
